import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent, EVENTS } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [statusMessage, setStatusMessage] = useState('Verifying payment...');

  // Helper function to check onboarding and redirect appropriately
  const checkOnboardingAndRedirect = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      const response = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const user = response.data;
      console.log('User onboarding status:', user.onboarding_completed);

      if (!user.onboarding_completed) {
        setStatusMessage('Setting up your profile...');
        toast.success('Payment successful! Let\'s set up your profile.');
        setTimeout(() => {
          navigate('/onboarding', { replace: true });
        }, 1500);
      } else {
        setStatusMessage('Redirecting to dashboard...');
        toast.success('Payment successful! Welcome to BragBox Pro!');
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to check user status:', error);
      // Default to onboarding if we can't determine status
      navigate('/onboarding', { replace: true });
    }
  };

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      // No session_id - Stripe didn't pass it or user navigated directly
      console.log('No session_id found - checking onboarding status');
      toast.success('Payment completed!');
      checkOnboardingAndRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const token = localStorage.getItem('session_token');
      
      if (!token) {
        console.log('No session token - redirecting to login');
        navigate('/login', { replace: true });
        return;
      }
      
      // Verify payment with backend
      const resp = await axios.get(
        `${API}/payments/checkout-status/${sessionId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (resp?.data?.payment_status === 'paid') {
        trackEvent(EVENTS.CHECKOUT_COMPLETED, { session_id: sessionId });
        // Fire Google Ads conversion (AW-18134588548 / Purchase) with revenue value
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          const planValueMap = { monthly: 14.99, yearly: 149.00, lifetime: 299.00, trial: 0 };
          const planId = (resp.data.metadata && resp.data.metadata.plan_id) || 'monthly';
          const value = planValueMap[planId] || 0;
          window.gtag('event', 'conversion', {
            send_to: 'AW-18134588548/ZtfWCOX-kKYcEIS5n8dD',
            value: value,
            currency: 'USD',
            transaction_id: sessionId
          });
        }
      }

      // Check onboarding status and redirect appropriately
      await checkOnboardingAndRedirect();
      
    } catch (error) {
      console.error('Payment verification failed:', error);
      // Still check onboarding even if verification fails
      toast.success('Payment completed!');
      await checkOnboardingAndRedirect();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--navy-50)' }}>
      <div className="text-center">
        <div className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#10B981' }}>
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
          Payment Successful!
        </h1>
        <p className="text-gray-600 mb-8">
          {statusMessage}
        </p>
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
